import React, { FC, useMemo } from "react";

import { usePagedRows } from "../../../components/pagination/usePagedRows";
import { exportDataToExcel } from "../../../utils/ExportData";
import { askTable } from "../ask/reportAsk.utils";
import { shortDate, throughputWeeks } from "../moreReports.utils";
import { scopeUserIds, useReportsAccess } from "../reportsAccess";
import { useViewExtra } from "../saved/SavedViewContext";
import { BarColumns } from "../ui/MoreCharts";
import {
  ChoiceMenu,
  PeopleClientTypeFilters,
  useReportFilters,
} from "../ui/ReportFilters";
import {
  Card,
  Kpi,
  KpiGrid,
  Legend,
  LoadError,
  Loading,
  Note,
  NUM,
  ReportPage,
  TableBox,
  TD,
  TH,
  useFocusRows,
} from "../ui/ReportUi";
import { useAllWorks } from "../useReportData";

const WEEK_OPTIONS = [8, 12, 26, 52].map((n) => ({
  value: String(n),
  label: `Last ${n} weeks`,
}));
const TABLE = "Per week";

/** Work started vs. finished (2026-11-05): is the backlog growing or shrinking? */
export const ThroughputReport: FC = () => {
  const access = useReportsAccess();
  const state = useReportFilters();
  const { filters } = state;
  const [weeksText, setWeeks] = useViewExtra("weeks", "12", WEEK_OPTIONS, "");
  const count = Number(weeksText) || 12;
  const works = useAllWorks({
    hideGreenStatus: false,
    userIds: scopeUserIds(access, filters.userIds),
    customerIds: filters.clientIds,
    worktypeIds: filters.workTypeIds,
  });
  const stats = useMemo(
    () => throughputWeeks(works.data, count),
    [works.data, count],
  );
  const change = stats.openNow - stats.openBefore;
  const v = (x: React.ReactNode) => (works.isLoading ? "…" : x);
  const shown = useFocusRows(TABLE, stats.weeks, (w) => shortDate(w.start));
  const newestFirst = useMemo(() => [...shown].reverse(), [shown]);
  const { pageRows, pager } = usePagedRows(newestFirst);

  const onExport = () =>
    exportDataToExcel(
      stats.weeks.map((w) => ({
        "Week of": shortDate(w.start),
        Started: w.started,
        Finished: w.finished,
        "Open at week end": w.openAtEnd,
      })),
      "Work started vs finished",
    );

  const ask = useMemo(
    () => ({
      title: "Work started vs. finished",
      path: "/reports/throughput",
      description: `Work items started and finished per week over the last ${count} weeks, and open work at each week end.`,
      kpis: [
        { label: "Started", value: String(stats.started) },
        { label: "Finished", value: String(stats.finished) },
        {
          label: "Backlog change",
          value: `${change >= 0 ? "+" : ""}${change}`,
        },
        {
          label: "Average time to finish",
          value: stats.averageDays === null ? "—" : `${stats.averageDays} days`,
        },
      ],
      tables: [
        askTable(
          TABLE,
          stats.weeks,
          [
            {
              key: "week",
              label: "Week of",
              kind: "date",
              value: (w) => shortDate(w.start),
            },
            {
              key: "started",
              label: "Started",
              kind: "number",
              value: (w) => w.started,
            },
            {
              key: "finished",
              label: "Finished",
              kind: "number",
              value: (w) => w.finished,
            },
            {
              key: "open",
              label: "Open at week end",
              kind: "number",
              value: (w) => w.openAtEnd,
            },
          ],
          (w) => shortDate(w.start),
        ),
      ],
    }),
    [stats, change, count],
  );

  return (
    <ReportPage
      title={"Work started vs. finished"}
      description={
        "New work items against completed ones, week by week, so you can see whether the backlog is growing or shrinking."
      }
      onExport={stats.weeks.length ? onExport : undefined}
      ask={works.isLoading ? undefined : ask}
      askSuggestions={[
        "Is the backlog growing?",
        "Which week did we finish the most?",
        "How long does work take to finish?",
      ]}
      filters={
        <>
          <ChoiceMenu
            label={"Show"}
            options={WEEK_OPTIONS}
            value={weeksText}
            onChange={setWeeks}
          />
          <PeopleClientTypeFilters state={state} showPeople={access.firmWide} />
        </>
      }>
      {works.isError && <LoadError />}
      <KpiGrid>
        <Kpi
          label={"Started"}
          value={v(stats.started)}
          note={"New work items"}
        />
        <Kpi
          label={"Finished"}
          value={v(stats.finished)}
          note={"Set to Completed"}
        />
        <Kpi
          label={"Backlog change"}
          value={v(`${change > 0 ? "+" : ""}${change}`)}
          note={`Open work ${change >= 0 ? "grew" : "shrank"} from ${
            stats.openBefore
          } to ${stats.openNow}`}
          tone={change > 0 ? "amber" : change < 0 ? "green" : "default"}
        />
        <Kpi
          label={"Average time to finish"}
          value={v(
            stats.averageDays === null ? "—" : `${stats.averageDays} days`,
          )}
          note={"Start to Completed"}
        />
      </KpiGrid>

      <Card
        title={TABLE}
        right={
          <Legend
            items={[
              { label: "Started", color: "#F79009" },
              { label: "Finished", color: "#6B4EFF" },
              {
                label: "Open work at week end (number on top)",
                color: "#3F4757",
              },
            ]}
          />
        }>
        {works.isLoading ? (
          <Loading />
        ) : (
          <BarColumns
            height={240}
            series={[
              { key: "started", label: "Started", color: "#F79009" },
              { key: "finished", label: "Finished", color: "#6B4EFF" },
            ]}
            buckets={stats.weeks.map((w) => ({
              key: w.start.toISOString(),
              label: w.label,
              values: [w.started, w.finished],
              top: String(w.openAtEnd),
            }))}
          />
        )}
      </Card>

      <Card title={"Week by week"}>
        {works.isLoading ? (
          <Loading />
        ) : (
          <TableBox pager={pager}>
            <thead>
              <tr>
                <th scope={"col"} className={TH}>
                  Week of
                </th>
                <th scope={"col"} className={`${TH} ${NUM}`}>
                  Started
                </th>
                <th scope={"col"} className={`${TH} ${NUM}`}>
                  Finished
                </th>
                <th scope={"col"} className={`${TH} ${NUM}`}>
                  Open at week end
                </th>
              </tr>
            </thead>
            <tbody>
              {pageRows.map((w) => (
                <tr key={w.start.toISOString()}>
                  <td className={`${TD} font-bold`}>{shortDate(w.start)}</td>
                  <td className={`${TD} ${NUM}`}>{w.started}</td>
                  <td className={`${TD} ${NUM}`}>{w.finished}</td>
                  <td className={`${TD} ${NUM} font-extrabold`}>
                    {w.openAtEnd}
                  </td>
                </tr>
              ))}
            </tbody>
          </TableBox>
        )}
      </Card>

      <Note>
        Number above each week = open work at the end of that week. Started =
        the work item&apos;s start date falls in that week; finished = it was
        set to Completed that week. Archived work isn&apos;t included.
        {!access.firmWide && " You see your own work."}
      </Note>
    </ReportPage>
  );
};
